import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import FeedPage from './feed';

const Header = () => {
  return (
    <header className="bg-white shadow">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        <h1 className="text-xl font-semibold text-indigo-900">Girls On Campus</h1>
        <nav>
          <ul className="flex space-x-4">
            <li>
              <Link to="/" className="text-gray-700 hover:text-indigo-500">Home</Link>
            </li>
            <li>
              <Link to="/feed" className="relative text-gray-700 hover:text-indigo-500">
                Explore Feed
                <span className="absolute top-[-8px] right-[-8px] w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

const HeroSection = () => {
  const [scale, setScale] = React.useState(1);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setScale(prev => prev === 1 ? 1.05 : 1);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 bg-gradient-to-b from-indigo-50 to-white text-center pb-8">
      <div className="max-w-5xl mx-auto px-6">
        <h1 className="text-4xl md:text-5xl font-semibold text-indigo-900 tracking-tight leading-tight mb-12">
          Let's build a strong college profile together
        </h1>
        <img 
          src="/hero_image.avif" 
          alt="Stock background"
          className="w-full max-w-2xl mx-auto mb-12 object-cover"
        />
        <style jsx>{`
          @keyframes subtlePulse {
            0%, 100% {
              box-shadow: 0 4px 6px rgba(79, 70, 229, 0.15);
            }
            50% {
              box-shadow: 0 4px 8px rgba(79, 70, 229, 0.25);
            }
          }
        `}</style>
        <Link
          to="/feed"
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-8 rounded-full transition-all duration-300"
          style={{
            animation: 'subtlePulse 3s infinite ease-in-out',
            boxShadow: '0 4px 6px rgba(79, 70, 229, 0.15)',
          }}
        >
          Join the club
        </Link>
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      title: "Discover Opportunities",
      description: "Find internships, competitions, and activities matched to your interests.",
      icon: {}
    },
    {
      title: "Access Resources",
      description: "Get guides, templates, and expert advice for your applications and essays.",
      icon: {}
    },
    {
      title: "Build Portfolio",
      description: "Join events, showcase projects, and build a compelling college profile.",
      icon: {}
    },
    {
      title: "Track Progress",
      description: "Monitor your journey and get help when you need it.",
      icon: {}
    },
  ];

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-semibold text-indigo-900 mb-8">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 flex flex-col h-full">
              <h3 className="text-xl font-semibold text-indigo-700 mb-2 whitespace-nowrap">{step.title}</h3>
              <p className="text-gray-600 text-sm">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const GoalsSection = () => {
  const goals = [
    {
      text: "Master Your Internship Journey",
      outcome: "Discover, Apply, Prepare, and Interview to secure your ideal high school internship.",
      products: [
        {
          name: "Discover: The Ultimate Internship Finder",
          description: "Unlock the best resources and strategies to discover high school internships that match your interests.",
          price: 29,
          link: "#",
        },
        {
          name: "Apply: Ace Your Internship Application",
          description: "Get proven templates and expert tips to craft compelling applications that get noticed.",
          price: 19,
          link: "#",
        },
        {
          name: "Prepare: Internship Interview Success Guide",
          description: "Learn how to confidently prepare for and ace your internship interviews.",
          price: 39,
          link: "#",
        },
        {
          name: "Free: Internship Application Tracker",
          description: "Stay organized and on top of your applications with our easy-to-use tracking sheet.",
          price: 0,
          link: "#",
        },
      ],
    },
    {
      text: "Live Competitions & Events",
      outcome: "Showcase your talents, participate in exciting live events, and win valuable prizes.",
      products: [
        {
          name: "Live Coding Challenge - March",
          description: "Join our free live coding competition happening this March and compete for exciting prizes!",
          price: 0,
          link: "#",
          details: "Date: March 25, 2025 (#coding, #programming)",
          actionText: "Participate",
        },
        {
          name: "Live Essay Writing Event - April",
          description: "Participate in our free live essay writing event in April for a chance to win amazing prizes!",
          price: 0,
          link: "#",
          details: "Date: April 10, 2025 (#essay, #writing)",
          actionText: "Participate",
        },
        {
          name: "Live Science Innovation Showcase - May",
          description: "Showcase your innovative science projects in our free live event in May and win recognition and prizes!",
          price: 0,
          link: "#",
          details: "Date: May 5, 2025 (#science, #research)",
          actionText: "Participate",
        },
      ],
    },
    {
      text: "Apply for Scholarships",
      outcome: "Get financial support for your educational goals.",
      products: [
        {
          name: "Scholarship Application Secrets",
          description: "Unlock the proven strategies and insider tips to significantly increase your chances of winning scholarships.",
          price: 49,
          link: "#",
        },
        {
          name: "Free Scholarship Directory",
          description: "Instantly access a curated and frequently updated list of scholarships specifically for high school students.",
          price: 0,
          link: "#",
        },
        {
          name: "Crafting a Killer Scholarship Essay",
          description: "Master the art of writing compelling and memorable scholarship essays that capture the attention of judges.",
          price: 59,
          link: "#",
        },
      ],
    },
    {
      text: "Improve SAT Scores",
      outcome: "Improve your SAT scores and increase your chances of getting into your dream college.",
      products: [
        {
          name: "SAT Math Mastery Bootcamp",
          description: "Intensive online bootcamp designed to help you master every concept on the SAT Math section.",
          price: 99,
          link: "#",
        },
        {
          name: "SAT Verbal Victory Bootcamp",
          description: "Comprehensive strategies and targeted practice to help you excel in the SAT Verbal section.",
          price: 99,
          link: "#",
        },
        {
          name: "Complete SAT Prep Bundle",
          description: "Get the ultimate package with everything you need to ace the entire SAT exam and achieve your target score.",
          price: 149,
          link: "#",
        },
      ],
    },
    {
      text: "Build Portfolio for College Apps",
      outcome: "Create a standout portfolio that effectively showcases your achievements and passions to college admissions.",
      products: [
        {
          name: "The Ultimate College Portfolio Builder",
          description: "A step-by-step guide that walks you through the entire process of creating a compelling college application portfolio.",
          price: 69,
          link: "#",
        },
        {
          name: "Portfolio Template Starter Kit",
          description: "Professionally designed and customizable portfolio templates to help you create a visually stunning presentation of your work.",
          price: 29,
          link: "#",
        },
        {
          name: "Personalized Portfolio Review & Feedback",
          description: "Get one-on-one expert feedback and actionable advice to take your college application portfolio to the next level.",
          price: 89,
          link: "#",
        },
      ],
    },
    {
      text: "Explore Career Paths",
      outcome: "Discover a wide range of potential career fields and gain clarity on the paths that align with your interests and skills.",
      products: [
        {
          name: "Career Exploration E-Book: Finding Your Fit",
          description: "An insightful e-book filled with information and exercises to help you explore various career options.",
          price: 0,
          link: "#",
        },
        {
          name: "Personalized Career Assessment",
          description: "Take our comprehensive career assessment to identify your strengths, interests, and potential career matches.",
          price: 39,
          link: "#",
        },
        {
          name: "The Guide to Informational Interviews",
          description: "Learn the art of conducting effective informational interviews to gain invaluable insights into different careers.",
          price: 19,
          link: "#",
        },
      ],
    },
  ];

  const [activeTab, setActiveTab] = React.useState(0);

  return (
    <section className="bg-white py-8">
      <div className="max-w-6xl mx-auto px-6">
        <p className="text-gray-700 mb-6">Explore our resources and opportunities to help you build an exceptional college application profile:</p>
        <div className="bg-indigo-50 rounded-lg shadow-md p-4 mb-6">
          <div className="flex overflow-x-auto gap-2 md:gap-4">
            {goals.map((goal, index) => (
              <button
                key={index}
                className={`rounded-full py-2 px-4 text-center text-indigo-700 font-semibold text-sm whitespace-nowrap transition duration-200 ${activeTab === index
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-indigo-100 hover:bg-indigo-200 hover:shadow-md'
                  }`}
                onClick={() => setActiveTab(index)}
              >
                {goal.text}
              </button>
            ))}
          </div>
        </div>

        {goals.length > 0 && goals[activeTab].products && goals[activeTab].products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {goals[activeTab].products.map((product, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
                <div className="p-4">
                  <h5 className="font-semibold text-indigo-900 mb-2">{product.name}</h5>
                  <p className="text-gray-600 text-sm mb-3">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-indigo-700 font-medium">
                      {product.price === 0 ? 'Free' : `$${product.price}`}
                    </span>
                    <a href={product.link} className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold text-sm py-2 px-4 rounded-full transition duration-200 shadow-md">
                      {product.actionText ? product.actionText : (product.price > 0 ? 'Buy Now' : 'Learn More')}
                    </a>
                  </div>
                  {product.details && (
                    <p className="mt-2 text-gray-500 text-xs">{product.details}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-indigo-50 rounded-lg shadow-md p-6 mt-4 text-center">
            <p className="text-gray-700">Stay tuned for more resources in this section!</p>
          </div>
        )}
      </div>
    </section>
  );
};

const App = () => {
  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
        <Header />
        <Routes>
          <Route path="/" element={<>
            <HeroSection />
            <HowItWorksSection />
            <GoalsSection />
          </>} />
          <Route path="/feed" element={<FeedPage />} />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
